"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

const IMAGE_BASE = "/image/new-version/molding/plastic-injection-molding/finishes";

const TAB_IDS = ["matte", "gloss", "texture", "directColoring", "secondary"] as const;

/** 占位宽高（仅用于 next/image 防 CLS；实际展示高度随各张 webp 自身比例，由 CSS h-auto 决定） */
const IMAGE_INTRINSIC = { width: 1200, height: 900 } as const;

/**
 * Plastic Injection Molding Finishes：5 Tab + 左文右图（/plastic-injection-molding）
 */
export default function PlasticInjectionMoldingFinishes() {
	const t = useTranslations("plasticInjectionMoldingFinishes");
	const [active, setActive] = useState(0);

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("intro")}</p>
				</div>

				{/*
				 * 分类切换
				 * - 仅「切换框」外轮廓：左 + 底（顶、右侧不要外边线）
				 * - 下方文案+图片区不再套同一外框线
				 * - Tab：激活 #76a734 + 白字；未激活 #f8f8f8；相邻竖分隔线
				 */}
				<div className="overflow-hidden rounded-none bg-white">
					{/* 切换框：左、底外边线；顶、右无 */}
					<div className="overflow-x-auto overflow-y-hidden border-b border-l border-[#dddddd] [-webkit-overflow-scrolling:touch]">
						<div className="inline-flex flex-nowrap justify-start border-r border-[#dddddd]">
							{TAB_IDS.map((id, index) => (
								<button
									key={id}
									type="button"
									onClick={() => setActive(index)}
									className={`shrink-0 whitespace-nowrap rounded-none border-r border-t border-[#dddddd] px-3 py-3 text-left text-xs leading-tight transition-colors last:border-r-0 sm:px-5 sm:py-3.5 sm:text-sm md:text-base ${
										active === index
											? "bg-[#76a734] font-bold text-white"
											: "bg-[#f8f8f8] font-normal text-gray-900 hover:bg-[#f0f0f0]"
									}`}
								>
									{t(`tab_${id}`)}
								</button>
							))}
						</div>
					</div>

					{/* 内容区：左文案 + 右图；大屏各占 50%；lg 下行等高 stretch，左列文案垂直居中 */}
					<div className="flex flex-col border border-[#dddddd] lg:flex-row lg:items-stretch">
						{/*
						 * min-w-0：避免 flex 子项长文本把 50% 撑破
						 * justify-center：与右列等高时，标题+段落在左列内垂直居中（图片仍按自身高度决定行高）
						 */}
						<div className="flex w-full min-w-0 flex-col justify-center self-stretch p-6 md:p-8 lg:w-1/2 lg:p-10">
							<h3 className="mb-4 text-lg font-bold text-black sm:text-xl md:text-2xl">
								{t(`contentTitle_${TAB_IDS[active]}`)}
							</h3>
							<p className="text-sm leading-relaxed text-black md:text-base">
								{t(`contentDesc_${TAB_IDS[active]}`)}
							</p>
						</div>
						{/*
						 * 图片列：宽度 50%，高度由图片文件本身比例决定（w-full + h-auto）
						 * - 不用 fill / 不与左列 stretch 等高，避免裁切或拉伸
						 * - width/height 仅作 next/image 占位；若各图比例差异大，可改为按索引配置真实像素
						 */}
						<div className="w-full min-w-0 p-6 md:p-8 lg:w-1/2 lg:p-10">
							<Image
								src={`${IMAGE_BASE}/${active + 1}.webp`}
								alt={t(`contentTitle_${TAB_IDS[active]}`)}
								width={IMAGE_INTRINSIC.width}
								height={IMAGE_INTRINSIC.height}
								className="h-auto w-full max-w-full"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
